/**
 * Este arquivo contém correções para problemas conhecidos do Bootstrap
 */

/**
 * Limpa elementos backdrop órfãos e modais que possam estar causando problemas
 * Esta função deve ser chamada quando ocorrem erros relacionados a modais
 * 
 * A limpeza é feita com cuidado para não interferir com elementos ativos.
 */
export const cleanupBootstrapModalElements = () => {
  // Verificar se existem modais abertos antes de limpar
  const openModals = document.querySelectorAll('.modal.show');
  
  // Se existirem modais abertos, não faça a limpeza para não afetar a funcionalidade
  if (openModals.length > 0) {
    return;
  }
  
  // Verificar se há dropdowns abertos (ativos)
  const openDropdowns = document.querySelectorAll('.dropdown-menu.show');
  
  // Se existirem dropdowns abertos, não faça a limpeza
  if (openDropdowns.length > 0) {
    return;
  }
  
  // Remover backdrops órfãos que possam ter ficado na página
  const backdrops = document.querySelectorAll('.modal-backdrop');
  backdrops.forEach(backdrop => {
    backdrop.classList.remove('show');
    setTimeout(() => {
      backdrop.remove();
    }, 300);
  });
  
  // Remover classes modal-open do body somente se não houver modais visíveis
  if (!document.querySelector('.modal.show')) {
    document.body.classList.remove('modal-open');
    
    // Remover estilos inline adicionados pelo Bootstrap somente se não houver modais visíveis
    // e não afetar os dropdowns
    if (document.body.style.overflow === 'hidden' && !document.querySelector('.dropdown-menu.show')) {
      document.body.style.overflow = '';
    }
    if (document.body.style.paddingRight && !document.querySelector('.dropdown-menu.show')) {
      document.body.style.paddingRight = '';
    }
  }
};

/**
 * Corrige o erro "Cannot read properties of undefined (reading 'backdrop')" que ocorre
 * quando um modal do Bootstrap é inicializado e a guia não está em foco.
 * 
 * Este erro acontece porque o Bootstrap tenta acessar propriedades de elementos que 
 * podem não estar totalmente carregados quando a página não está em foco ou quando
 * o usuário muda entre guias do navegador.
 */
export const fixBootstrapModalBackdropIssue = () => {
  // Uma variável para rastrear se já aplicamos o patch
  let patchApplied = false;
  
  // Função para garantir que os componentes dropdown funcionem corretamente
  const ensureDropdownsWork = () => {
    if (typeof window !== 'undefined' && window.bootstrap) {
      // Verificar se já existe um Bootstrap Dropdown
      const dropdownElements = document.querySelectorAll('[data-bs-toggle="dropdown"]');
      if (dropdownElements.length > 0 && window.bootstrap && window.bootstrap.Dropdown) {
        // Garantir que os dropdowns funcionem
        dropdownElements.forEach(el => {
          // Verifica se o dropdown já foi inicializado
          if (!window.bootstrap.Dropdown.getInstance(el)) {
            try {
              // Inicializa o dropdown
              new window.bootstrap.Dropdown(el);
            } catch (err) {
              console.warn('Erro ao inicializar dropdown:', err);
            }
          }
        });
      }
    }
  };
  
  // Função para aplicar o patch de forma segura apenas quando necessário
  const applyFix = () => {
    // Se já aplicamos o patch, não faça nada
    if (patchApplied) return;
    
    // Garante que os dropdowns funcionem antes de aplicar o patch
    ensureDropdownsWork();
    
    if (typeof window !== 'undefined' && window.bootstrap && window.bootstrap.Modal) {
      // Guarda referências originais para restauração se necessário
      const originalInitBackdrop = window.bootstrap.Modal.prototype._initializeBackDrop;
      
      // Cria um patch específico apenas para o método que está causando o erro
      window.bootstrap.Modal.prototype._initializeBackDrop = function() {
        try {
          // Verifica se temos o que precisamos antes de continuar
          if (this._config === undefined) {
            console.warn('Modal _config indefinido, evitando erro');
            // Retorna um objeto mínimo para evitar erros
            return {
              show: () => {},
              hide: () => {},
              dispose: () => {}
            };
          }
          
          // Tentativa de chamar o método original
          return originalInitBackdrop.apply(this, arguments);
        } catch (err) {
          // Se ocorrer um erro, registramos e retornamos um objeto simples
          console.warn('Erro no Bootstrap Modal resolvido:', err);
          return {
            show: () => {},
            hide: () => {},
            dispose: () => {}
          };
        }
      };

      // Marcamos que o patch foi aplicado
      patchApplied = true;
      console.log('Correção específica para o erro de backdrop do Modal aplicada');
    } else {
      // Se o Bootstrap ainda não foi carregado, tente novamente em breve
      setTimeout(applyFix, 500);
    }
  };

  // Aplica a correção inicial com cuidado para não interferir com os dropdowns
  setTimeout(() => {
    // Primeiro garanta que os dropdowns funcionem
    ensureDropdownsWork();
    
    // Depois limpe apenas backdrops órfãos sem interferir em outros componentes
    const backdrops = document.querySelectorAll('.modal-backdrop');
    if (backdrops.length > 0 && !document.querySelector('.modal.show') && 
        !document.querySelector('.dropdown-menu.show')) {
      cleanupBootstrapModalElements();
    }
    
    // Por fim, aplica o patch específico para o erro de backdrop
    applyFix();
  }, 500); // Aumentamos o tempo para garantir que o Bootstrap esteja carregado

  // Adiciona um listener para quando a página volta a ter foco
  window.addEventListener('focus', () => {
    // Quando a página volta a ter foco, garantimos que os dropdowns funcionem
    ensureDropdownsWork();
    
    // Depois verificamos se há erros
    setTimeout(() => {
      // Verifica se há backdrops sem modais ativos e sem dropdowns ativos
      const backdrops = document.querySelectorAll('.modal-backdrop');
      const activeModals = document.querySelectorAll('.modal.show');
      const activeDropdowns = document.querySelectorAll('.dropdown-menu.show');
      
      if (backdrops.length > 0 && activeModals.length === 0 && activeDropdowns.length === 0) {
        cleanupBootstrapModalElements();
      }
    }, 300);
  });
  
  // Adiciona um listener específico para erros de backdrop
  window.addEventListener('error', (event) => {
    // Verifica se o erro está relacionado ao backdrop do modal
    if (event.error && event.error.message && 
        event.error.message.includes('backdrop') && 
        event.error.message.includes('undefined')) {
      console.warn('Erro específico de backdrop detectado');
      
      // Primeiro garantir que os dropdowns funcionem
      ensureDropdownsWork();
      
      // Aplica a correção apenas para este erro específico
      applyFix();
      
      // Limpa apenas elementos de backdrop problemáticos, se não houver dropdowns ativos
      if (!document.querySelector('.dropdown-menu.show')) {
        cleanupBootstrapModalElements();
      }
      
      // Impede que o erro seja propagado para o console
      event.preventDefault();
    }
  });
};
